import axios from 'axios'
import { nanoid } from 'nanoid'
import { captureException, handleLoadReCaptcha } from '~/common/helpers'
import { action, computed, observable, persist, storage, store } from '~/common/mobx.decorator'
import { authStore } from './auth.store'
import { THEMES } from '~/themes'
import { apolloClient, GET_APP_INIT_QUERY, GET_USER_INFO_QUERY } from '~/common/apollo'
import { COMPANY_CODE } from '~/common/constants/company.constant'

axios.interceptors.response.use(undefined, (error) => {
	captureException('Axios Client', error)
	return Promise.reject(error)
})

@store({ persist: true })
class AppStore {
	constructor() {
		storage.ready(() => {
			this.ready = true
			if (!this._uniqueId) {
				this._uniqueId = nanoid()
			}
		})
	}

	@persist @observable _uniqueId
	@observable config = null
	@observable ready = false

	@observable liveChatId
	@observable meta_domain

	@observable appConfig
	@observable appPublicConfig

	@observable tenantId = ''
	@observable apiUrl = ''
	@observable themeConfig = null
	@observable recaptchaLoaded = false

	@action
	setTenantId = (tenantId) => {
		this.tenantId = tenantId
		sessionStorage.setItem('tenantId', tenantId)
	}

	@action
	setApiUrl = (apiUrl) => {
		this.apiUrl = apiUrl
		sessionStorage.setItem('apiUrl', apiUrl)
		axios.defaults.baseURL = `${apiUrl}/api/public/v1`
	}

	@action
	setThemeConfig = (themeConfig) => {
		this.themeConfig = themeConfig

		// Set global config for recaptcha access without circular import
		if (!window.__OPUS_APP_CONFIG__) {
			window.__OPUS_APP_CONFIG__ = {}
		}

		const newKey = themeConfig?.config?.REACT_APP_RECAPTCHA_KEY
		const oldKey = window.__OPUS_APP_CONFIG__.recaptchaKey

		window.__OPUS_APP_CONFIG__.recaptchaKey = newKey
		window.__OPUS_APP_CONFIG__.apiReady = true

		// Trigger reload of reCAPTCHA script if key changed and already loaded
		if (typeof window !== 'undefined' && oldKey && oldKey !== newKey && this.recaptchaLoaded) {
			const existingScript = document.getElementById('recaptcha-key')
			if (existingScript) {
				existingScript.remove()
				this.recaptchaLoaded = false // Allow reload
			}
		}

		// Auto-load reCAPTCHA when config is set
		this.tryLoadReCaptcha()
	}

	@action
	tryLoadReCaptcha = async () => {
		if (this.isApiConfigReady && !this.recaptchaLoaded) {
			try {
				await this.loadReCaptcha()
			} catch (error) {
				// Silent fail - will retry when needed
			}
		}
	}

	@action
	loadReCaptcha = async () => {
		if (this.recaptchaLoaded) return

		this.recaptchaLoaded = true
		try {
			await handleLoadReCaptcha()
		} catch (error) {
			this.recaptchaLoaded = false // Reset on error to allow retry
			throw error
		}
	}

	@computed
	get hideHotJobCandidateSetting() {
		return this.code === COMPANY_CODE.lsr || this.code === COMPANY_CODE.crossmed || this.code === COMPANY_CODE.concentric
	}

	@computed
	get recaptchaKey() {
		return this.themeConfig?.config?.REACT_APP_RECAPTCHA_KEY || process.env.REACT_APP_RECAPTCHA_KEY
	}

	@computed
	get isRecaptchaReady() {
		return this.recaptchaLoaded && !!document.getElementById('recaptcha-key')
	}

	@computed
	get isApiConfigReady() {
		// API config is ready when fetchTenantConfig completed
		// This is confirmed by having apiUrl set AND either themeConfig OR apiReady flag
		const hasApiUrl = this.apiUrl !== ''
		const hasThemeConfig = !!this.themeConfig
		const apiMarkedReady = window.__OPUS_APP_CONFIG__?.apiReady === true

		return hasApiUrl && (hasThemeConfig || apiMarkedReady)
	}

	@computed
	get isEnableAA() {
		return this.appConfig?.tenantConfig?.enableOpusAiAssistant || false
	}

	@computed
	get tenantConfig() {
		return this.appConfig.tenantConfig || {}
	}

	@computed
	get enableMatchesFeedback() {
		if (this.appConfig) {
			const enableMatchesFeedback = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_enable_matches_feedback')
			if (enableMatchesFeedback && enableMatchesFeedback.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableUpcomingWorkerAssignments() {
		if (this.appConfig) {
			const enableUpcomingWorkerAssignments = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_upcoming_worker_assignments')
			if (enableUpcomingWorkerAssignments && enableUpcomingWorkerAssignments.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableReportAndAnalytics() {
		if (this.appConfig) {
			const reportAndAnalyticsFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'reports_and_analytics')
			if (reportAndAnalyticsFeature && reportAndAnalyticsFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableViewAsCandidate() {
		if (this.appConfig) {
			const viewAsCandidateFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'view_as_candidate')
			if (viewAsCandidateFeature && viewAsCandidateFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableEmployeeLogout() {
		if (this.appConfig) {
			const employeeLogoutFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'employer_logout')
			if (employeeLogoutFeature && employeeLogoutFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableDeleteResume() {
		if (this.appConfig) {
			const deleteResumeFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'delete_resume')
			if (deleteResumeFeature && deleteResumeFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableWorkerDisciplineSpecialties() {
		if (this.appConfig?.tenantConfig) {
			return this.appConfig?.tenantConfig?.specialtyAssociateWithDiscipline
		}
		return false
	}

	@computed
	get enableChangeEmployeeRole() {
		if (this.appConfig) {
			const changeEmployeeRoleFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'change_employer_role')
			if (changeEmployeeRoleFeature && changeEmployeeRoleFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableSystemNotificationSettings() {
		if (this.appConfig) {
			const systemNotificationSettingsFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'system_setting_notifications')
			if (systemNotificationSettingsFeature && systemNotificationSettingsFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableProfileCompletion() {
		if (this.appConfig) {
			const profileCompletionFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'profile_completion')
			if (profileCompletionFeature && profileCompletionFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enablePerDiemEligible() {
		if (this.appConfig) {
			const perDiemEligibleFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_per_diem_eligible_worker')
			if (perDiemEligibleFeature && perDiemEligibleFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableStateLicenseColumn() {
		// if (this.appConfig) {
		// 	const stateLicenseColumnFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_state_license_column')
		// 	if (stateLicenseColumnFeature && stateLicenseColumnFeature.enabled) {
		// 		return true
		// 	}
		// }
		// return false
		return true
	}
	/* Company Id  */
	@computed
	get id() {
		return process.env.REACT_APP_COMPANY_ID
	}

	@computed
	get name() {
		return this.appConfig?.tenant?.name || process.env.REACT_APP_COMPANY_NAME
	}

	@computed
	get code() {
		return this.appConfig?.tenant?.tenantCode
	}

	@computed
	get logo() {
		if (this.appConfig?.tenant?.logo?.file?.fileUrl) {
			return this.appConfig?.tenant?.logo?.file?.fileUrl
		}
		return `${process.env.PUBLIC_URL}/assets/defaultLogo.png`
	}

	@computed
	get publicLogo() {
		if (this.appPublicConfig?.tenant?.logo?.file?.fileUrl) {
			return this.appPublicConfig?.tenant?.logo?.file?.fileUrl
		}
		return this.logos.logoClient
	}

	@computed
	get logos() {
		return {
			logo: this.logo,
			logoClient: `${process.env.PUBLIC_URL}/assets/logoClient.png?version=${process.env.REACT_APP_CI_COMMIT_TAG}`,
			logiMini: `${process.env.PUBLIC_URL}/assets/logo-mini.png?version=${process.env.REACT_APP_CI_COMMIT_TAG}`,
		}
	}
	@computed
	get getTheme() {
		return THEMES.aequor
	}
	@action
	init = async () => {
		// Fetch tenant config first for better performance
		await this.fetchTenantConfig()

		await Promise.allSettled([this.fetchFEConfig()]).catch(console.error)

		setTimeout(() => {
			authStore.verifyToken()
		}, 1000)
	}

	fetchFEConfig = async () => {
		const response = await fetch('/app-config.json')
		return await response.json()
	}

	@action
	fetchAppConfig = async () => {
		try {
			const response = await apolloClient().query({
				query: GET_APP_INIT_QUERY,
			})
			this.appConfig = response.data?.appInit || {}
		} catch (e) {
			console.error(e)
			throw e
		}
	}

	@action
	fetchPublicAppConfig = async () => {
		try {
			const response = await apolloClient().query({
				query: GET_APP_INIT_QUERY,
				context: { clientName: 'public' },
			})
			this.appPublicConfig = response.data?.appInit || {}
		} catch (e) {
			console.error(e)
			throw e
		}
	}

	@action
	getProfileInfo = async () => {
		try {
			if (authStore.authorization) {
				const data = await apolloClient().query({
					query: GET_USER_INFO_QUERY,
				})

				if (data?.employer) {
					this.profile = data.employer
					this.roles = data.employer?.roles
				}
			}
		} catch (err) {
			console.error(err)
		}
	}

	@action
	fetchTenantConfig = async () => {
		const isDevelopment = window.location.hostname === 'localhost'
		const isGHRorLSR = process.env.REACT_APP_LOCAL_COMPANY_NAME === 'GHR' || process.env.REACT_APP_LOCAL_COMPANY_NAME === 'LSR'
		try {
			if (isDevelopment && isGHRorLSR) {
				this.setApiUrl('https://lsr-backend-qa.opusasia.io')
				// Mark API as ready even for special development case
				if (!window.__OPUS_APP_CONFIG__) {
					window.__OPUS_APP_CONFIG__ = {}
				}
				window.__OPUS_APP_CONFIG__.apiReady = true

				// Auto-load reCAPTCHA for development case
				this.tryLoadReCaptcha()
				return
			}
			const headerConfig = isDevelopment
				? {
						headers: {
							'tenant-id': process.env.REACT_APP_DEFAULT_TENANT_ID || '',
							Accept: 'application/json',
						},
				  }
				: {
						headers: {
							Accept: 'application/json',
						},
				  }

			const response = await axios.get(`${process.env.REACT_APP_PROXY_API_URL}/api/public/v1/connections`, headerConfig)
			if (response?.data) {
				this.setTenantId(response.data.tenantId)
				this.setApiUrl(`https://${response.data.apiDomain}`)
				if (response.data.themeConfig) {
					this.setThemeConfig(response.data.themeConfig)
				} else {
					// Still set a basic config so we know API call completed
					if (!window.__OPUS_APP_CONFIG__) {
						window.__OPUS_APP_CONFIG__ = {}
					}
					window.__OPUS_APP_CONFIG__.apiReady = true

					// Auto-load reCAPTCHA even without themeConfig
					this.tryLoadReCaptcha()
				}
			}
		} catch (e) {
			console.error(e)
		}
	}
}

export const appStore = new AppStore()
